import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";

const Division = ({
  divisionGroups,
  divisionName,
  answers,
  handleAnswerChange,
  isExpanded,
  onToggleExpand,
}) => {
  return (
    <div className="mb-4 border-b pb-2">
      <div
        className="flex justify-between items-center cursor-pointer mb-2"
        onClick={onToggleExpand}
      >
        <h2 className="text-lg font-bold text-[#06513D] flex items-center">
          {isExpanded ? (
            <ChevronDown size={18} className="mr-2" />
          ) : (
            <ChevronRight size={18} className="mr-2" />
          )}
          {divisionName}
        </h2>
      </div>

      {isExpanded && (
        <div className="pl-4 space-y-4">
          {renderQuestions(divisionGroups, answers, handleAnswerChange)}
        </div>
      )}
    </div>
  );
};
const ChildQuestionCard = ({
  optionLabel,
  optionValue,
  question,
  allQuestions,
  questionNumber,
  answers,
  handleAnswerChange,
  renderQuestionRecursively,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const childQuestions = allQuestions.filter(
    (child) =>
      child.condition &&
      child.condition.questionId === question.questionId &&
      child.condition.value === optionValue,
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
      <div
        className={`${isExpanded ? "bg-[#054030] text-white rounded-t-lg" : "border border-[#054030] text-[#054030] rounded-lg"}  px-4 py-3  cursor-pointer flex justify-between items-center  transition-colors`}
        onClick={toggleExpand}
      >
        <span className="font-medium">{optionLabel}</span>
        <button type="button" className="text-xl font-bold">
          {isExpanded ? "-" : "+"}
        </button>
      </div>

      {isExpanded && (
        <div className="p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {childQuestions
              .filter((child) => child.type !== "title")
              .map((child, idx) =>
                renderQuestionRecursively(
                  child,
                  allQuestions,
                  `${questionNumber}.${idx + 1}`,
                  answers,
                  handleAnswerChange,
                ),
              )}
          </div>
        </div>
      )}
    </div>
  );
};

const MultiSelectPicklist = ({
  question,
  allQuestions,
  questionNumber,
  answers,
  handleAnswerChange,
  renderQuestionRecursively,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showGoToTop, setShowGoToTop] = useState(false);
  const selectedValues = answers[question.questionId] || [];
  const dropdownRef = useRef(null);
  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleOptionToggle = (optValue) => {
    handleAnswerChange(question.questionId, optValue, "multi-select");
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setShowGoToTop(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const getSelectedChips = () => {
    return selectedValues.map((value) => {
      const option = question.options.find((opt) => opt.value === value);
      return option ? option.key : value;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="mb-6 border rounded-lg shadow p-4">
      <label className="block mb-2 font-medium">
        Q.{questionNumber} {question.label}
      </label>

      <div className="relative" ref={dropdownRef}>
        <div
          className="border border-gray-300 rounded px-3 py-2 cursor-pointer bg-white flex justify-between items-center min-h-[40px]"
          onClick={toggleDropdown}
        >
          <div className="flex-1">
            {selectedValues.length === 0 ? (
              <span className="text-gray-400">Select options...</span>
            ) : selectedValues.length <= 7 ? (
              <div className="flex flex-wrap gap-1">
                {getSelectedChips().map((chip, index) => (
                  <span
                    key={index}
                    className="bg-[#06513D] text-white px-2 py-1 rounded text-sm"
                  >
                    {chip}
                    <button
                      type="button"
                      className="ml-2 text-white hover:text-gray-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionToggle(selectedValues[index]);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-700">
                {selectedValues.length} items selected
              </span>
            )}
          </div>
          <ChevronDown
            size={20}
            className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
            {question.options.map((opt) => {
              const isSelected = selectedValues.includes(opt.value);

              return (
                <div key={opt._id}>
                  <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b">
                    <label className="flex items-center cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleOptionToggle(opt.value)}
                        className="mr-2 text-[#06513D] accent-[#06513D]"
                      />
                      <span className="text-gray-700">{opt.key}</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {selectedValues.map((selectedValue) => {
          const selectedOption = question.options.find(
            (opt) => opt.value === selectedValue,
          );
          const hasChildQuestions = allQuestions.some(
            (child) =>
              child.condition &&
              child.condition.questionId === question.questionId &&
              child.condition.value === selectedValue &&
              child.type !== "title",
          );

          if (!hasChildQuestions) return null;

          return (
            <ChildQuestionCard
              key={selectedValue}
              optionLabel={selectedOption ? selectedOption.key : selectedValue}
              optionValue={selectedValue}
              question={question}
              allQuestions={allQuestions}
              questionNumber={questionNumber}
              answers={answers}
              handleAnswerChange={handleAnswerChange}
              renderQuestionRecursively={renderQuestionRecursively}
            />
          );
        })}
      </div>
    </div>
  );
};

const renderQuestionRecursively = (
  question,
  allQuestions,
  questionNumber,
  answers,
  handleAnswerChange,
) => {
  const value = answers[question.questionId];

  if (question.type === "multi-select") {
    return (
      <MultiSelectPicklist
        key={question.questionId}
        question={question}
        allQuestions={allQuestions}
        questionNumber={questionNumber}
        answers={answers}
        handleAnswerChange={handleAnswerChange}
        renderQuestionRecursively={renderQuestionRecursively}
      />
    );
  }

  const tableChildren = allQuestions.filter(
    (child) =>
      child.condition &&
      child.condition.questionId === question.questionId &&
      child.condition.value === "123",
  );

  const shouldShowTable = question.type === "title" && tableChildren.length > 0;

  return (
    <div
      key={question.questionId}
      className={`mt-3 ${questionNumber.includes(".") ? "ml-4 bg-gray-50 border p-3 rounded" : "mb-6 border rounded-lg shadow p-4"}`}
    >
      <label className="block mb-1 font-medium">
        Q{questionNumber} {question.label}
      </label>

      {renderInput(question, answers, handleAnswerChange)}

      {shouldShowTable && (
        <div className="mt-4 overflow-x-auto bg-gray-50 p-3 rounded">
          <table className="table-auto border-collapse w-full">
            <tbody>
              {Array.from({ length: Math.ceil(tableChildren.length / 2) }).map(
                (_, rowIndex) => {
                  const rowChildren = tableChildren.slice(
                    rowIndex * 2,
                    rowIndex * 2 + 2,
                  );

                  return (
                    <React.Fragment key={rowIndex}>
                      {rowChildren.map((child, idx) => {
                        if (child.type === "title") {
                          return (
                            <tr key={child.questionId} className="bg-[#fff]">
                              <th
                                colSpan={2}
                                className="border px-4 py-2 text-center text-[#06513D] font-light"
                              >
                                {child.label}
                              </th>
                            </tr>
                          );
                        }

                        if (idx === 0) {
                          return (
                            <React.Fragment key={rowIndex}>
                              <tr className="bg-[#06513D]">
                                {rowChildren.map((c) =>
                                  c.type !== "title" ? (
                                    <th
                                      key={c.questionId}
                                      className="border px-4 py-2 text-left text-[#fff] font-light"
                                    >
                                      {c.label}
                                    </th>
                                  ) : null,
                                )}
                              </tr>
                              <tr>
                                {rowChildren.map((c) =>
                                  c.type !== "title" ? (
                                    <td
                                      key={c.questionId}
                                      className="border px-4 py-2"
                                    >
                                      {renderInput(
                                        c,
                                        answers,
                                        handleAnswerChange,
                                      )}
                                      {Array.isArray(c.options) &&
                                        c.options.some(
                                          (opt) => opt.value === "Other",
                                        ) &&
                                        answers[c.questionId] === "Other" && (
                                          <div className="mt-1 gap-2 flex w-40">
                                            <input
                                              type="text"
                                              placeholder="Enter name"
                                              value={
                                                answers[
                                                  `${c.questionId}_other_name`
                                                ] || ""
                                              }
                                              onChange={(e) =>
                                                handleAnswerChange(
                                                  `${c.questionId}_other_name`,
                                                  e.target.value,
                                                )
                                              }
                                              className=" w-36 px-3 py-1 border border-gray-300 rounded text-xs"
                                            />
                                            <input
                                              type="text"
                                              placeholder="Enter value"
                                              value={
                                                answers[
                                                  `${c.questionId}_other_value`
                                                ] || ""
                                              }
                                              onChange={(e) =>
                                                handleAnswerChange(
                                                  `${c.questionId}_other_value`,
                                                  e.target.value,
                                                )
                                              }
                                              className=" w-36 px-3 py-1 border border-gray-300 rounded text-xs"
                                            />
                                          </div>
                                        )}
                                    </td>
                                  ) : null,
                                )}
                              </tr>
                            </React.Fragment>
                          );
                        }
                        return null;
                      })}
                    </React.Fragment>
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      )}

      {allQuestions
        .filter(
          (child) =>
            child.condition &&
            child.condition.questionId === question.questionId &&
            (question.type === "multi-select"
              ? Array.isArray(value) && value.includes(child.condition.value)
              : value === child.condition.value),
        )
        .map((child, idx) =>
          renderQuestionRecursively(
            child,
            allQuestions,
            `${questionNumber}.${idx + 1}`,
            answers,
            handleAnswerChange,
          ),
        )}
    </div>
  );
};

const renderInput = (question, answers, handleAnswerChange) => {
  if (question.type === "text") {
    return (
      <textarea
        type="text"
        className="border p-2 w-full"
        placeholder="Please Enter the text"
        style={{
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          minHeight: "10px",
          height: "auto",
        }}
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const currentValue = e.target.value;
            const cursorPos = e.target.selectionStart;
            const newValue =
              currentValue.slice(0, cursorPos) +
              "\n" +
              currentValue.slice(cursorPos);
            handleAnswerChange(question.questionId, newValue);

            setTimeout(() => {
              e.target.setSelectionRange(cursorPos + 1, cursorPos + 1);
            }, 0);
          }
        }}
      />
    );
  }

  if (question.type === "number") {
    return (
      <input
        type="number"
        className="border p-2 w-full"
        placeholder="Please enter the number"
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      />
    );
  }

  if (question.type === "file") {
    return (
      <input
        type="file"
        className="border p-2 w-full"
        placeholder="Please upload file"
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.files[0])
        }
      />
    );
  }

  if (question.type === "select") {
    return (
      <select
        className="border p-2 w-full"
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      >
        <option value="">Select</option>
        {question.options.map((opt) => (
          <option key={opt.key} value={opt.value}>
            {opt.value}
          </option>
        ))}
      </select>
    );
  }

  if (question.type === "multi-select") {
    return (
      <div className="space-y-2">
        {question.options.map((opt) => {
          const isChecked = (answers[question.questionId] || []).includes(
            opt.value,
          );
          return (
            <div key={opt._id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() =>
                  handleAnswerChange(
                    question.questionId,
                    opt.value,
                    "multi-select",
                  )
                }
              />
              <span>{opt.key}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

const renderQuestions = (divisionGroups, answers, handleAnswerChange) => {
  let parentCounter = 0;

  return divisionGroups.map((group) => {
    const rootQuestions = group.questions.filter((q) => !q.condition);

    return rootQuestions.map((question) => {
      parentCounter++;
      const questionNumber = parentCounter;

      return renderQuestionRecursively(
        question,
        group.questions,
        questionNumber.toString(),
        answers,
        handleAnswerChange,
      );
    });
  });
};

export const QuestionForm = ({ questions, expandAll }) => {
  const [expandedIds, setExpandedIds] = useState([]);
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [answers, setAnswers] = useState({});
  // Missing state for question selection
  const [selectedQuestions, setSelectedQuestions] = useState({});

  // Missing function to handle question selection
  const toggleQuestionSelect = (questionId) => {
    setSelectedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  useEffect(() => {
    const groupedDivisions = questions.reduce((acc, curr) => {
      const division = curr.division || "Untitled Division";
      if (!acc[division]) acc[division] = [];
      acc[division].push(curr);
      return acc;
    }, {});

    const initialExpanded = {};
    Object.keys(groupedDivisions).forEach((division) => {
      initialExpanded[division] = false;
    });
    setExpandedDivisions(initialExpanded);
  }, [questions]);

  useEffect(() => {
    if (expandAll !== undefined && questions.length > 0) {
      const groupedDivisions = questions.reduce((acc, curr) => {
        const division = curr.division || "Untitled Division";
        if (!acc[division]) acc[division] = [];
        acc[division].push(curr);
        return acc;
      }, {});

      const allExpanded = {};
      Object.keys(groupedDivisions).forEach((division) => {
        allExpanded[division] = expandAll;
      });
      setExpandedDivisions(allExpanded);

      if (expandAll) {
        const allIds = questions.map((group) => group.id);
        setExpandedIds(allIds);
      } else {
        setExpandedIds([]);
      }
    }
  }, [expandAll, questions]);

  const toggleDivisionExpand = (divisionName) => {
    setExpandedDivisions((prev) => ({
      ...prev,
      [divisionName]: !prev[divisionName],
    }));
  };

  const handleAnswerChange = (questionId, value, type = "text") => {
    setAnswers((prev) => {
      if (type === "multi-select") {
        const prevArray = prev[questionId] || [];
        const newArray = prevArray.includes(value)
          ? prevArray.filter((v) => v !== value)
          : [...prevArray, value];
        return { ...prev, [questionId]: newArray };
      }
      return { ...prev, [questionId]: value };
    });
  };

  const groupedDivisions = questions.reduce((acc, curr) => {
    const division = curr.division || "Untitled Division";
    if (!acc[division]) acc[division] = [];
    acc[division].push(curr);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto p-6 font-inter">
      <div>
        {Object.entries(groupedDivisions).map(
          ([divisionName, divisionGroups]) => (
            <Division
              key={divisionName}
              divisionGroups={divisionGroups}
              divisionName={divisionName}
              answers={answers}
              handleAnswerChange={handleAnswerChange}
              isExpanded={expandedDivisions[divisionName]}
              onToggleExpand={() => toggleDivisionExpand(divisionName)}
              // selectedQuestions={selectedQuestions} // ✅ Added
              // toggleQuestionSelect={toggleQuestionSelect} // ✅ Added
            />
          ),
        )}
      </div>
    </div>
  );
};
